import styled, { css, type DefaultTheme } from 'styled-components';
import Colours from '/shared/styled/Colours';
import { baseFontWeight } from '/shared/styled/GlobalStyle/GlobalStyle';

interface Props {
  theme?: DefaultTheme;
  $inline?: boolean;
  $light?: boolean;
  $grey?: boolean;
  $white?: boolean;
  $green?: boolean;
  $bold?: boolean;
  $smallText?: boolean;
  $extraSmallText?: boolean;
  $largeText?: boolean;
  $lightFont?: boolean;
  $leadText?: boolean;
  $lightWeight?: boolean;
  $hasExtraMarginBottom?: boolean;
  $hasSomeMarginTop?: boolean;
  $hasExtraMarginTop?: boolean;
  $hasSomeMarginBottom?: boolean;
  $noMargin?: boolean;
  $styleLinks?: boolean;
  $linkColour?: string;
}

function lightStyle({ theme, $light = false }: Props) {
  if ($light && theme?.target.isBusiness === false) {
    return css`
      ${baseFontWeight}
    `;
  }
}

function greyStyle({ $grey = false }: Props) {
  if ($grey) {
    return css`
      color: ${Colours.grey};
    `;
  }
}

function whiteStyle({ $white = false }: Props) {
  if ($white) {
    return css`
      color: ${Colours.white};
    `;
  }
}

function greenStyle({ $green = false }: Props) {
  if ($green) {
    return css`
      color: ${Colours.green};
    `;
  }
}

function smallTextStyle({ $smallText = false }: Props) {
  if ($smallText) {
    return css`
      font-size: 0.9rem;
      line-height: 1.6;
    `;
  }
}
function extraSmallTextStyle({ $extraSmallText = false }: Props) {
  if ($extraSmallText) {
    return css`
      font-size: 0.8rem;
      line-height: 1.7;
    `;
  }
}

function largeTextStyle({ $largeText = false }: Props) {
  if ($largeText) {
    return css`
      font-size: 16px;
      line-height: 1.6;
      letter-spacing: 0.2px;
    `;
  }
}

function inlineStyle({ $inline = false }: Props) {
  if ($inline) {
    return css`
      display: inline;
    `;
  }
}

function boldStyle({ $bold = false }: Props) {
  if ($bold) {
    return css`
      font-weight: 400;
    `;
  }
}

function lightWeightStyle({ $lightWeight = false }: Props) {
  if ($lightWeight) {
    return css`
      font-weight: 300;
    `;
  }
}

function styleLinkStyle({ $styleLinks = true, $linkColour }: Props) {
  let colour = Colours.blackDark1;
  if ($linkColour) {
    colour = $linkColour;
  }
  if ($styleLinks) {
    return css`
      a,
      a:visited {
        border-bottom: 2px solid ${Colours.blackDark1};
        color: ${colour} !important;
        text-decoration: none;

        &:hover,
        &:focus,
        &:active,
        &:visited:hover,
        &:visited:focus,
        &:visited:active {
          color: ${colour};
        }
      }
    `;
  }
  return css`
    a,
    a:visited {
      color: ${colour} !important;
      text-decoration: none;

      &:hover,
      &:focus,
      &:active,
      &:visited:hover,
      &:visited:focus,
      &:visited:active {
        color: ${Colours.green} !important;
      }
    }
  `;
}

function leadTextStyle({ $leadText = false }: Props) {
  if ($leadText) {
    return css`
      font-size: 20px;
      font-weight: 300;
    `;
  }
}

function lightFontStyle({ $lightFont = false }: Props) {
  if ($lightFont) {
    return css`
      font-weight: 300;
    `;
  }
}

function hasSomeMarginTopStyle({ $hasSomeMarginTop = false }: Props) {
  if ($hasSomeMarginTop) {
    return css`
      margin-top: 1rem;
    `;
  }
}

function hasSomeMarginBottomStyle({ $hasSomeMarginBottom = false }: Props) {
  if ($hasSomeMarginBottom) {
    return css`
      margin-bottom: 0.5rem !important;
    `;
  }
}

function hasExtraMarginBottomStyle({ $hasExtraMarginBottom = false }: Props) {
  if ($hasExtraMarginBottom) {
    return css`
      margin-bottom: 2rem !important;
    `;
  }
}

function hasExtraMarginTopStyle({ $hasExtraMarginTop = false }: Props) {
  if ($hasExtraMarginTop) {
    return css`
      margin-top: 2rem;
    `;
  }
}

function hasNoMargin({ $noMargin = false }: Props) {
  if ($noMargin) {
    return css`
      margin: 0 !important;
    `;
  }
}

export default styled.p<Props>`
  font-size: 1rem;
  letter-spacing: 0.01em;
  margin: 0;
  padding: 0;
  line-height: 1.6;
  font-weight: 300;

  &:not(:last-of-type) {
    margin-bottom: 1rem;
  }

  ${inlineStyle}
  ${lightStyle}
  ${greyStyle}
  ${whiteStyle}
  ${greenStyle}
  ${boldStyle}
  ${styleLinkStyle}
  ${smallTextStyle}
  ${extraSmallTextStyle}
  ${leadTextStyle}
  ${largeTextStyle}
  ${lightFontStyle}
  ${lightWeightStyle}
  ${hasExtraMarginBottomStyle}
  ${hasSomeMarginTopStyle}
  ${hasExtraMarginTopStyle}
  ${hasSomeMarginBottomStyle}
  ${hasNoMargin}
`;
